import { useCallback, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { motion, useAnimationControls } from 'framer-motion';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useEventRotation } from '../../../../../components/LobbyPassBanner/useEventRotation';
import type { BingoResultToShowType, MetagameBingoEvent } from '../../../../../stores/bingo/types';
import { BINGO_WIDGET_TIMER_BUFFER_MS } from '../../../constants';
import { useBingoAnimationReady, useBingoWidgetCallbacks, useWidgetAdaptiveText } from '../../../hooks';
import type { BingoWidgetDataType } from '../../../types';
import { BingoWidgetLogo } from '../BingoWidgetLogo/BingoWidgetLogo';
import { BingoWidgetTimer } from '../BingoWidgetTimer/BingoWidgetTimer';

import styles from './BingoSmallWidget.module.scss';

interface BingoSmallWidgetProps {
  data: BingoWidgetDataType;
  newPlayer?: boolean;
}

const RewardWidget = ({ event }: { event: BingoResultToShowType }) => {
  const { onResultClick } = useBingoWidgetCallbacks();
  const { textRef: topTextRef } = useWidgetAdaptiveText({ padding: 2 });
  const { textRef: bottomTextRef } = useWidgetAdaptiveText({ padding: 2 });
  const { t } = useTranslation();
  if (!event) {
    return null;
  }

  return (
    <div className={styles.container} role="button" onClick={() => onResultClick(event)}>
      <BingoWidgetLogo timerIncluded bingoType={event.coinType === 'GC' ? 'CASUAL' : 'SWEEP'} sizeType={'small'} />
      <div className={styles.contentContainer}>
        <div className={styles.seePrizeTextContainer}>
          <span ref={topTextRef} className={styles.seePrizeText}>
            {t('prime.latestBingoResult.youWonUpper')}
          </span>
        </div>
        <button className={styles.seePrizeBtn}>
          <span ref={bottomTextRef} className={styles.btnText}>
            {t('prime.latestBingoResult.seePrize')}
          </span>
        </button>
      </div>
    </div>
  );
};

const LiveWidget = ({ event }: { event: MetagameBingoEvent }) => {
  const { onEnterLiveEventClick } = useBingoWidgetCallbacks();
  const { t } = useTranslation();
  return (
    <div className={styles.container} role="button" onClick={() => onEnterLiveEventClick(event)}>
      <BingoWidgetLogo liveButtonIncluded bingoType={event.coinType === 'GC' ? 'CASUAL' : 'SWEEP'} sizeType="small" />
      <div className={styles.liveBtn}>{t('prime.lobbyBanner.enter')}</div>
    </div>
  );
};

const UpcomingWidget = observer(({ newPlayer }: { newPlayer?: boolean }) => {
  const { shouldRotate, upcomingEvents, displayEvent } = useEventRotation();
  const animationReadyRef = useBingoAnimationReady();
  const { onPurchaseClick } = useBingoWidgetCallbacks();
  const { t } = useTranslation();

  if (!upcomingEvents || !upcomingEvents.length) {
    return null;
  }

  const rotationEnabled = shouldRotate && !!displayEvent;
  const currentEvent = rotationEnabled ? displayEvent : upcomingEvents[0];
  const bufferedStartMs = currentEvent?.startDate
    ? new Date(currentEvent.startDate).getTime() + BINGO_WIDGET_TIMER_BUFFER_MS
    : null;
  const timeToStartInMs = bufferedStartMs ? dayjs(bufferedStartMs).diff(dayjs(), 'milliseconds') : 0;
  const bingoType = currentEvent.coinType === 'GC' ? 'CASUAL' : 'SWEEP';
  const [deferredTimer, setDeferredTimer] = useState({ eventId: currentEvent?.id || '', startsInMs: timeToStartInMs });
  const latestTimerRef = useRef({ eventId: currentEvent?.id || '', startsInMs: timeToStartInMs });
  latestTimerRef.current = { eventId: currentEvent?.id || '', startsInMs: timeToStartInMs };

  const timerControls = useAnimationControls();

  const disappear = useCallback(async () => {
    await timerControls.start({ scale: [1, 1.1, 0], transition: { duration: 1, ease: 'easeInOut' } });
  }, [timerControls]);

  const appear = useCallback(async () => {
    await timerControls.start({ scale: [0, 1.1, 1], transition: { duration: 1, ease: 'easeInOut' } });
  }, [timerControls]);

  const rotate = useCallback(async () => {
    await disappear();
    setDeferredTimer(latestTimerRef.current);
    await appear();
  }, [disappear, appear]);

  useEffect(() => {
    if (!animationReadyRef.current) {
      return;
    }
    if (rotationEnabled) {
      void rotate();
    }
  }, [rotate, rotationEnabled, animationReadyRef, currentEvent?.id]);

  return (
    <div className={styles.container} role="button" onClick={onPurchaseClick}>
      <BingoWidgetLogo
        timerIncluded
        animationReadyRef={animationReadyRef}
        bingoType={bingoType}
        rotation={rotationEnabled}
        sizeType={'small'}
      />
      <motion.div animate={timerControls} className={styles.contentContainer}>
        <BingoWidgetTimer bingoType={bingoType} eventId={deferredTimer.eventId} startsInMs={deferredTimer.startsInMs} />
        <button className={styles.btn}>
          <span className={styles.btnText}>{newPlayer ? t('prime.lobbyBanner.joinNow') : t('plan.purchase')}</span>
        </button>
      </motion.div>
    </div>
  );
});

export function BingoSmallWidget(props: BingoSmallWidgetProps) {
  const { data, newPlayer } = props;
  const { event, widgetType } = data;

  switch (widgetType) {
    case 'reward':
      return <RewardWidget event={event as BingoResultToShowType} />;
    case 'upcoming':
      return <UpcomingWidget newPlayer={newPlayer} />;
    case 'live':
    case 'startingSoon':
      return <LiveWidget event={event as MetagameBingoEvent} />;
    default:
      return null;
  }
}
